import React, { useState, useEffect, useMemo } from "react";
import {
  CarouselProvider,
  Slider,
  Slide,
  ButtonBack,
  ButtonNext,
  Image,
} from "pure-react-carousel";
import "pure-react-carousel/dist/react-carousel.es.css";
import { Button } from "../../components/tailwind";
import { Icon } from "../../components/tailwind";
import { ProductEnrichImage } from "../../api/enrichApi";

interface ImageCarouselProps {
  images: ProductEnrichImage[];
  onActiveImageChange?: (imageUrl: string) => void;
  onDeleteActiveImage?: (imageUrl: string) => void;
}

export const ImageCarousel: React.FC<ImageCarouselProps> = ({
  images: unsortedImages,
  onActiveImageChange,
  onDeleteActiveImage,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Included images first, so the ones actually sent to the AI lead the carousel.
  const images = useMemo(
    () =>
      [...unsortedImages].sort(
        (a, b) => Number(b.included) - Number(a.included)
      ),
    [unsortedImages]
  );

  useEffect(() => {
    if (images.length === 0) {
      return;
    }
    const maxSlide = images.length - 1;
    const nextSlide = Math.min(currentSlide, maxSlide);
    if (nextSlide !== currentSlide) {
      setCurrentSlide(nextSlide);
      return;
    }
    onActiveImageChange?.(images[nextSlide].url);
  }, [currentSlide, images, onActiveImageChange]);

  if (images.length === 0) {
    return null;
  }

  return (
    <div className="tw-relative">
      <CarouselProvider
        naturalSlideWidth={100}
        naturalSlideHeight={100}
        totalSlides={images.length}
        isIntrinsicHeight={true}
        currentSlide={currentSlide}
      >
        <div className="tw-relative">
          <Slider>
            {images.map(({ url, included }, index) => (
              <Slide index={index} key={url}>
                <div className="tw-relative tw-flex tw-justify-center tw-items-center tw-bg-gray-50 tw-rounded tw-p-2">
                  <Image
                    src={url}
                    hasMasterSpinner={true}
                    className="tw-max-h-64 tw-w-auto tw-object-contain"
                    renderError={() => (
                      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-32 tw-w-32 tw-text-gray-400">
                        <Icon name="broken_image" />
                        <span className="tw-text-xs tw-mt-1">
                          Image unavailable
                        </span>
                      </div>
                    )}
                  />
                  {!included && (
                    <div
                      className="tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center tw-rounded"
                      style={{ backgroundColor: "rgba(17, 24, 39, 0.7)" }}
                      data-balloon="This image has been excluded by the configured image filter and will not be sent to the AI when Enriching."
                      data-balloon-pos="top"
                      data-balloon-length="medium"
                    >
                      <span className="tw-text-white tw-text-base tw-font-semibold tw-uppercase tw-tracking-wide">
                        Excluded
                      </span>
                    </div>
                  )}
                  {onDeleteActiveImage && (
                    <Button
                      type="button"
                      variant="secondary-alt"
                      icon
                      onClick={() => onDeleteActiveImage(url)}
                      className="tw-absolute tw-bottom-2 tw-right-2 tw-z-10 tw-bg-white/90 tw-shadow-sm"
                      data-balloon="Delete image"
                      data-balloon-pos="left"
                    >
                      <Icon name="delete" />
                    </Button>
                  )}
                </div>
              </Slide>
            ))}
          </Slider>

          {/* Navigation Buttons */}
          {images.length > 1 && (
            <>
              <ButtonBack
                onClick={() => setCurrentSlide((prev) => Math.max(0, prev - 1))}
                className="tw-absolute tw-left-2 tw-top-1/2 tw-transform -tw-translate-y-1/2 tw-bg-white tw-rounded-full tw-p-2 tw-shadow-md hover:tw-bg-gray-100 tw-border tw-border-gray-300"
              >
                <svg
                  className="tw-w-5 tw-h-5 tw-text-gray-700"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </ButtonBack>
              <ButtonNext
                onClick={() =>
                  setCurrentSlide((prev) =>
                    Math.min(images.length - 1, prev + 1)
                  )
                }
                className="tw-absolute tw-right-2 tw-top-1/2 tw-transform -tw-translate-y-1/2 tw-bg-white tw-rounded-full tw-p-2 tw-shadow-md hover:tw-bg-gray-100 tw-border tw-border-gray-300"
              >
                <svg
                  className="tw-w-5 tw-h-5 tw-text-gray-700"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </ButtonNext>
            </>
          )}
        </div>

        {/* Slide Indicators */}
        {images.length > 1 && (
          <div className="tw-flex tw-justify-center tw-gap-2 tw-mt-3">
            {images.map(({ url }, index) => (
              <button
                key={url}
                onClick={() => setCurrentSlide(index)}
                className={`tw-w-2 tw-h-2 tw-rounded-full tw-transition-colors ${
                  currentSlide === index
                    ? "tw-bg-blue-600"
                    : "tw-bg-gray-300 hover:tw-bg-gray-400"
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </CarouselProvider>
    </div>
  );
};
